import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, themeClasses } from '@member-portal/ui';
import { clsx } from 'clsx';
import type { RequestFormPayload, RequestModalProps } from './types';

export function RequestModal({
  isOpen,
  onClose,
  onSubmit,
  initialPreferredHotel,
  vendorId,
}: RequestModalProps) {
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState<number | ''>('');
  const [numberOfRooms, setNumberOfRooms] = useState<number | ''>('');
  const [numberOfChildren, setNumberOfChildren] = useState<number | ''>('');
  const [roomType, setRoomType] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const resetForm = () => {
    setGuestFirstName('');
    setGuestLastName('');
    setArrivalDate('');
    setDepartureDate('');
    setNumberOfGuests('');
    setNumberOfRooms('');
    setNumberOfChildren('');
    setRoomType('');
    setSpecialRequests('');
    setShowError(false);
    setErrorMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);

    try {
      const payload: RequestFormPayload = {
        requestType: 'Hotel',
        vendorId,
        guestFirstName: guestFirstName.trim() || undefined,
        guestLastName: guestLastName.trim() || undefined,
        arrivalDate: arrivalDate || undefined,
        departureDate: departureDate || undefined,
        numberOfGuests: numberOfGuests === '' ? undefined : Number(numberOfGuests),
        numberOfRooms: numberOfRooms === '' ? undefined : Number(numberOfRooms),
        numberOfChildren: numberOfChildren === '' ? undefined : Number(numberOfChildren),
        roomType: roomType.trim(),
        specialRequests: specialRequests.trim() || undefined,
      };
      await onSubmit(payload);
      setSuccessMessage('Your booking request has been submitted successfully!');
      setShowSuccess(true);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit request. Please try again.';
      setErrorMessage(message);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };

  const handleErrorDismiss = () => {
    setShowError(false);
  };

  const canSubmit = !!vendorId && roomType.trim().length > 0;

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
            onClick={handleClose}
            aria-hidden
          />
          <div
            className={clsx(
              'relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all',
              'w-full max-w-2xl max-h-[90vh] overflow-y-auto',
              themeClasses.colors.bg.primary,
              themeClasses.colors.border.secondary,
              'border'
            )}
          >
            <div className="px-8 pt-8 pb-6">
              <div className="flex items-center justify-between">
                <h3 className={clsx(themeClasses.title.h4, 'text-lg')}>
                  Request Booking
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  className={clsx(
                    themeClasses.colors.text.muted,
                    themeClasses.colors.hover.text,
                    'h-6 w-6 flex items-center justify-center transition-colors duration-300'
                  )}
                  aria-label="Close"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-8 pb-8">
              <div className="space-y-6">
                {/* Hotel name display */}
                {initialPreferredHotel && (
                  <div>
                    <label className={clsx(themeClasses.text.muted, 'block mb-2 text-sm')}>
                      Hotel
                    </label>
                    <p className={clsx(themeClasses.text.body)}>{initialPreferredHotel}</p>
                  </div>
                )}

                {/* Guest Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Guest first name
                    </label>
                    <input
                      type="text"
                      value={guestFirstName}
                      onChange={(e) => setGuestFirstName(e.target.value)}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Guest last name
                    </label>
                    <input
                      type="text"
                      value={guestLastName}
                      onChange={(e) => setGuestLastName(e.target.value)}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Arrival date
                    </label>
                    <input
                      type="date"
                      value={arrivalDate}
                      onChange={(e) => setArrivalDate(e.target.value)}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                    />
                  </div>
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Departure date
                    </label>
                    <input
                      type="date"
                      value={departureDate}
                      onChange={(e) => setDepartureDate(e.target.value)}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                    />
                  </div>
                </div>

                {/* Guests, Rooms, Children */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Number of guests
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={numberOfGuests === '' ? '' : numberOfGuests}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNumberOfGuests(v === '' ? '' : parseInt(v, 10));
                      }}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Number of accommodations
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={numberOfRooms === '' ? '' : numberOfRooms}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNumberOfRooms(v === '' ? '' : parseInt(v, 10));
                      }}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                      placeholder="e.g. 1"
                    />
                  </div>
                  <div>
                    <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                      Number of children
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={numberOfChildren === '' ? '' : numberOfChildren}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNumberOfChildren(v === '' ? '' : parseInt(v, 10));
                      }}
                      className={clsx(
                        themeClasses.form.input,
                        themeClasses.colors.bg.secondary,
                        themeClasses.colors.border.primary,
                        themeClasses.colors.text.primary,
                        'rounded-md'
                      )}
                      placeholder="e.g. 0"
                    />
                  </div>
                </div>

                {/* Accommodation type (required) */}
                <div>
                  <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                    Accommodation Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={roomType}
                    onChange={(e) => setRoomType(e.target.value)}
                    required
                    className={clsx(
                      themeClasses.form.input,
                      themeClasses.colors.bg.secondary,
                      themeClasses.colors.border.primary,
                      themeClasses.colors.text.primary,
                      'rounded-md w-full'
                    )}
                    placeholder="e.g. King, two doubles, suite"
                  />
                </div>

                {/* Special Requests */}
                <div>
                  <label className={clsx(themeClasses.text.secondary, 'block mb-2')}>
                    Special requests
                  </label>
                  <textarea
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    rows={3}
                    className={clsx(
                      themeClasses.form.textarea,
                      themeClasses.colors.bg.secondary,
                      themeClasses.colors.border.primary,
                      themeClasses.colors.text.primary,
                      'rounded-md'
                    )}
                    placeholder="Any special requests or preferences"
                  />
                </div>

                {showError && (
                  <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                    <button
                      type="button"
                      onClick={handleErrorDismiss}
                      className="mt-2 text-sm underline text-red-600 dark:text-red-400"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 mt-8">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting || !canSubmit}>
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={handleSuccessClose}
            aria-hidden
          />
          <div
            className={clsx(
              'relative rounded-lg shadow-xl p-8 max-w-md text-center',
              themeClasses.colors.bg.primary,
              themeClasses.colors.border.primary,
              'border'
            )}
          >
            <p className={clsx(themeClasses.text.body, 'mb-6')}>{successMessage}</p>
            <Button type="button" variant="primary" onClick={handleSuccessClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </>
  );

  return typeof window !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
